import { useCallback, type ChangeEvent, useState } from 'react';
import { TextInput } from '@cbhq/cds-web/controls';
import { Switch } from '@cbhq/cds-web/controls';
import { TextLabel1 } from '@cbhq/cds-web/typography';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { useGlobalSettings } from './GlobalSettingsProvider';
import { DEFAULT_SLIPPAGE_SETTINGS } from './defaultSettings';

const SLIPPAGE_VALIDATION_MIN = 0;
const SLIPPAGE_VALIDATION_MAX = 100;
const SLIPPAGE_WARNING_UPPER_BOUND = 1;

const SLIPPAGE_SETTINGS_INPUT_TEST_ID = 'slippage-settings-input-field';
const SLIPPAGE_SETTINGS_SWITCH_TEST_ID = 'slippage-settings-switch-field';

// Hoist static RegExp pattern to avoid recreating on every render
const SLIPPAGE_DECIMAL_REGEX = /^\d*\.?\d{0,2}/;

export type SlippageSettingValue = {
  maxSlippagePercent: string;
  autoMaxSlippage: boolean;
};

export type SlippageSettingProps = {
  label: string;
  settingKey: string;
};

export function SlippageSetting({ label, settingKey }: SlippageSettingProps) {
  const { getSettingValue, updateSetting } = useGlobalSettings();

  const value = getSettingValue(settingKey) as SlippageSettingValue;
  const [maxSlippage, setMaxSlippage] = useState(value?.maxSlippagePercent);

  // Get current values from props with defaults
  const isAutoSlippage = value?.autoMaxSlippage ?? false;

  const handleEditMaxSlippage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setMaxSlippage(inputValue);
      if (
        inputValue !== '' &&
        !isAutoSlippage &&
        !isNaN(Number(inputValue)) &&
        Number(inputValue) >= SLIPPAGE_VALIDATION_MIN &&
        Number(inputValue) <= SLIPPAGE_VALIDATION_MAX
      ) {
        // Match decimal places up to 2, preserve trailing zeros
        const match = inputValue.match(SLIPPAGE_DECIMAL_REGEX);
        if (match) {
          const newValue = match[0];
          updateSetting(settingKey, {
            ...value,
            maxSlippagePercent: newValue,
          });
        }
      }
    },
    [isAutoSlippage, updateSetting, value.maxSlippagePercent, settingKey],
  );

  const handleToggleAutoSlippage = useCallback(() => {
    const newAutoSlippage = !isAutoSlippage;
    const newMaxSlippagePercent = newAutoSlippage
      ? DEFAULT_SLIPPAGE_SETTINGS.maxSlippagePercent
      : value.maxSlippagePercent;
    setMaxSlippage(newMaxSlippagePercent);
    updateSetting(settingKey, {
      maxSlippagePercent: newMaxSlippagePercent,
      autoMaxSlippage: newAutoSlippage,
    });
  }, [isAutoSlippage, updateSetting, value.maxSlippagePercent, settingKey]);

  const showSlippageWarning = () => {
    if (!isAutoSlippage && Number(maxSlippage) > SLIPPAGE_WARNING_UPPER_BOUND) {
      return true;
    }
    return false;
  };

  return (
    <VStack gap={2}>
      <HStack gap={1} alignItems="center">
        <TextLabel1 as="p">{label}</TextLabel1>
        <Tooltip
          content="Your transaction will revert if the price changes unfavorably by more than this percentage."
          placement="top"
        >
          <Icon name="info" size="xs" color="fgMuted" active />
        </Tooltip>
      </HStack>
      <HStack alignItems="center">
        <TextInput
          testID={SLIPPAGE_SETTINGS_INPUT_TEST_ID}
          compact
          disabled={isAutoSlippage}
          type="number"
          step="0.01"
          value={maxSlippage}
          onChange={handleEditMaxSlippage}
          placeholder="0.0"
          helperText={
            showSlippageWarning()
              ? `Your transaction may be front-run, resulting in an unfavorable trade. Consider decreasing your slippage tolerance to less than ${SLIPPAGE_WARNING_UPPER_BOUND}%`
              : undefined
          }
          variant={showSlippageWarning() ? 'negative' : undefined}
          suffix="%"
        />
        <HStack style={{ scale: 0.8, transformOrigin: 'center right' }}>
          <Switch
            testID={SLIPPAGE_SETTINGS_SWITCH_TEST_ID}
            onChange={handleToggleAutoSlippage}
            checked={isAutoSlippage}
          >
            Default
          </Switch>
        </HStack>
      </HStack>
    </VStack>
  );
}
